import React, { useEffect, useState } from 'react';
import { FunnelCart } from 'src/ts/types/cart';
import CartFooter from './CartFooter';
import { useDiscount } from 'src/context/discount';
import SummaryDrawer from './SummaryDrawer/SummaryDrawer';
import { Plan } from 'src/services/cart-order/plans-service';

interface CartFooterAndSummaryProps {
  cart: FunnelCart;
  setCart: React.Dispatch<React.SetStateAction<FunnelCart | null>>;
  addMealToCart(productVariantId: number, cartDeliveryId: number): void;
  removeMealFromCart(productVariantId: number, cartDeliveryId: number): void;
  onContinueButtonClick: () => void;
  soldOutItemVariantMap: Record<string, Array<number>>;
  selectedPlan?: Plan;
  createdPopulatedFromPersonalizedPlan?: boolean;
}

const CartFooterAndSummary: React.FC<CartFooterAndSummaryProps> = ({
  cart,
  setCart,
  addMealToCart,
  removeMealFromCart,
  onContinueButtonClick,
  soldOutItemVariantMap,
  selectedPlan,
  createdPopulatedFromPersonalizedPlan,
}) => {
  // If the user comes into the funnel page with a plan selected and the cart hasn't been previously
  // modified, we want to show them the drawer open. We don't have any other use-cases for this
  // feature so I'm just leaving this logic here.
  const summaryDrawerDefaultOpen =
    ((selectedPlan || createdPopulatedFromPersonalizedPlan) &&
      !cart.modifiedDuringFunnel) ??
    false;

  const [isSummaryDrawerOpen, setIsSummaryDrawerOpen] = useState(
    summaryDrawerDefaultOpen,
  );

  const [expandedDeliveryId, setExpandedDeliveryId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    // This useEffect is purposfully only firing when the drawer is closed. Otherwise
    // when you add or remove in the drawer, the accordians can open and close while
    // interacting with them
    //
    // The logic here is a bit weird with this whole `!summaryDrawerDefaultOpen` since
    // when this component is initially loaded for Plans, we need an item to be selected.
    // With the logic as it was, this useEffect would never fire. Since the first item
    // a user adds or removes will change `cart.modifiedDuringFunnel` this logic works.
    if (isSummaryDrawerOpen && !summaryDrawerDefaultOpen) {
      return;
    }

    const firstWithItems = cart.cartDeliveries.find(
      (d) => d.lineItems.length > 0,
    );
    setExpandedDeliveryId(firstWithItems?.id ?? null);
  }, [cart, isSummaryDrawerOpen]);

  const discountEligibility = useDiscount();

  if (!cart || !cart.cartLedger) {
    return null;
  }

  const totalNumMeals =
    cart?.cartDeliveries.reduce(
      (total, delivery) =>
        total +
        delivery.lineItems.reduce(
          (deliveryTotal, lineItem) => deliveryTotal + lineItem.quantity,
          0,
        ),
      0,
    ) ?? 0;

  // The reason these values are being differentiated as 'display' vs 'strikethrough' has to do with how
  // the ledger values change when a discount is validly applied. For a universally applied discount this
  // values are static. But if you have a minimum-meal style discount, the grand total gets altered to
  // relfect that discount amount when you hit the minimum.
  const displayTotal =
    cart.cartLedger.grandTotal - cart.cartLedger.deliveryTotal;
  const strikethroughTotal = !!cart.cartLedger.discountTotal
    ? cart.cartLedger.subtotal
    : undefined;

  return (
    <>
      <CartFooter
        displayTotal={displayTotal ?? 0}
        strikethroughTotal={strikethroughTotal}
        totalNumMeals={totalNumMeals}
        toggleDrawerOpen={() => setIsSummaryDrawerOpen(!isSummaryDrawerOpen)}
        totalMealMinimumCopy={
          discountEligibility.eligibleDiscount?.totalMealMinimumCopy
        }
      />
      <SummaryDrawer
        open={isSummaryDrawerOpen}
        onClose={() => {
          setIsSummaryDrawerOpen(false);
        }}
        cart={cart}
        setCart={setCart}
        addMealToCart={addMealToCart}
        removeMealFromCart={removeMealFromCart}
        onContinueButtonClick={onContinueButtonClick}
        soldOutItemVariantMap={soldOutItemVariantMap}
        selectedPlan={selectedPlan}
        expandedDeliveryId={expandedDeliveryId}
        setExpandedDeliveryId={setExpandedDeliveryId}
        cartCreatedFromPersonalizedPlan={cart?.createdFromPersonalizedPlan}
      />
    </>
  );
};

export default CartFooterAndSummary;
